<template>
  <div class="slideshow" :class="aspectClass">
    <div id="pixi-container" class="slideshow-container" :class="aspectClass">
      <canvas class="slideshow-canvas" id="slideshow-canvas" ref="slideshowCanvas"></canvas>
    </div>

    <div class="position-absolute" style="right:0;top:0;">
      <div v-if="recorderTimer" class="bg-danger text-white p-2 rounded" @click="stopRecording" >
        <i class="bi bi-circle-fill mr-2 recording"></i>
        RECORDING
      </div>
      <div v-if="loading" class="bg-warning text-white p-2 rounded" >
        <i class="bi bi-arrow-clockwise mr-2 recording"></i>
        LOADING
      </div>
    </div>


  </div>

</template>

<script setup>
import * as PIXI from 'pixi.js'
import { gsap } from "gsap"
import { SlowMo } from "gsap/EasePack";

gsap.registerPlugin(SlowMo);

import {computed, defineProps, onMounted, onBeforeUnmount, nextTick, ref} from "vue" // useTemplateRef, ref,
// import { getInventoryImageUrl } from '@/composables/useInventoryImages'
const slideshowCanvas = ref(null)
const recorderTimer = ref(null)
const loading = ref(true)

let stream, recorder

const props = defineProps({
  items: {
    type: Array,
    required: true,
    default: () => []
  },
  holdDuration: {
    type: Number,
    default: 6000
  },
  fadeDuration: {
    type: Number,
    default: 500
  },
  transition: {
    type: String,
    default: 'crossfade'
  },
  randomOrder: {
    type: Boolean,
    default: false
  },
  aspectRatio: {
    type: Number,
    default: 1.0
  },
  showTitle: {
    type: Boolean,
    default: true
  },
  showPrice: {
    type: Boolean,
    default: true
  },
  showSold: {
    type: Boolean,
    default: true
  },
  bgColor: {
    type: String,
    default: '#DE3249'
  },
  ease: {
    type: String,
    default: 'elastic.out(0.6, 0.4)' // 'power4.inOut' // : 'linear'
  },
  record: {
    type: Boolean,
    default: false
  }
})

const slides = computed(() => props.items
  .map(item => ({
    title: item.title,
    sale_price: item.sale_price,
    photo: item.thumbnail, // getInventoryImageUrl(item, 'large'),
    date: new Date(item.create_date).toLocaleDateString(),
    is_sold: Array.isArray(item.is_sold) ? item.is_sold.length > 0 : (item.is_sold > 0),
  }))
  .filter(item => item.photo) // Only include items with valid photos
  .sort(() => props.randomOrder ? Math.floor(Math.random()*3)-2 : 0)
)

const aspectClass = computed(() => {
  if (props.aspectRatio < 1) return 'landscape'
  if (props.aspectRatio > 1) return 'portrait'
  return 'square'
})

let app = null;
let timeline = null;


function stopRecording() {
  if (recorderTimer.value) {
    clearTimeout(recorderTimer.value);
    recorder.stop()
  }
}

onMounted(async () => {
  await nextTick()
  // save current params to local storage
  const newDefaults = {
    holdDuration: props.holdDuration,
    fadeDuration: props.fadeDuration,
    transition: props.transition,
    randomOrder: props.randomOrder,
    aspectRatio: props.aspectRatio,
    showTitle: props.showTitle,
    showPrice: props.showPrice,
    showSold: props.showSold,
    bgColor: props.bgColor,
    ease: props.ease,
    record: props.record
  }
  window.localStorage.setItem('esc_slideshow', JSON.stringify(newDefaults))

  const canvas = slideshowCanvas.value
  const size = {x:1280, y:Math.floor(1280 * props.aspectRatio)}
  const padding = Math.floor(size.x / 25)

  app = new PIXI.Application();
  await app.init({ width: size.x, height: size.y, canvas: canvas, backgroundAlpha: 0 });
  app.cullable = true
  //app.useAlphaContext = false
  //app.antialias = false
  //document.getElementById('pixi-container').appendChild(app.view);

  const bg = new PIXI.Graphics();
  bg.rect(0, 0, size.x, size.y);
  bg.fill(props.bgColor);
  app.stage.addChild(bg)

  //const scaleFactor = size.x / 1600
  const fadeD = props.fadeDuration / 1000
  const holdD = props.holdDuration / 1000
  const ease = props.ease

  const gsapParams = [];

  for(let i = 0; i < slides.value.length; i++) {
    const slide = new PIXI.Container();
    slide.width=size.x
    slide.height=size.y
    slide.interactiveChildren = false
    slide.visibe = false
    app.stage.addChild(slide);

    const graphics = new PIXI.Graphics();
    graphics.rect(0, 0, size.x, size.y);
    graphics.fill(props.bgColor);
    slide.addChild(graphics)

    try {
      const texture = await PIXI.Assets.load(slides.value[i].photo);
      if (texture) {
        const sprite = new PIXI.Sprite(texture)
        sprite.width = Math.min(size.x, size.y)
        sprite.height = Math.min(size.x, size.y)
        sprite.position.x = aspectClass.value === 'landscape' ? 0 : (slide.width / 2) - (sprite.width / 2);
        sprite.position.y = (slide.height / 2) - (sprite.height / 2);
        slide.addChild(sprite);
      } else {
        console.warn('Failed to load texture for:', slides.value[i].photo);
      }
    } catch (error) {
      console.error('Error loading texture:', slides.value[i].photo, error);
    }

    const text = new PIXI.Text(slides.value[i].title, {
      fontFamily: 'Arial Black',
      fontSize: Math.floor(padding * (Math.abs(props.aspectRatio - 0.8) + 0.6)),
      //lineHeight: Math.floor(padding * 1.5 * props.aspectRatio ),
      fill: 0xffffff,
      wordWrap: true,
      wordWrapWidth: (aspectClass.value === 'landscape' ? size.x - size.y : size.x) - padding * 2,
      align: 'left',
      lineJoin: 'round',
      miterLimit: 3,
      padding: Math.floor(padding / 2),
      dropShadow: true,
      dropShadowColor: 'black',
      dropShadowBlur: Math.floor(padding / 5),
      stroke: 0x000000,
      strokeThickness: Math.floor(padding / 6),
    });

    text.x = (aspectClass.value === 'landscape' ? size.y : 0 ) + padding
    text.y = padding - Math.floor(padding / 3)


    if (props.showTitle) slide.addChild(text);

    if (props.showSold && slides.value[i].is_sold) {
      const sold = new PIXI.Text('SOLD', {
        fontFamily: 'Arial Black',
        fontSize: padding * 4,
        fill: 0xff0000,
        align: 'center',
        miterLimit: 2,
        padding: Math.floor(padding),
        dropShadow: true,
        dropShadowColor: 'black',
        dropShadowBlur: Math.floor(padding),
        stroke: 0xffffff,
        strokeThickness: Math.floor(padding / 3),
      })
      sold.x = size.x/2;
      sold.y = size.y/2;
      //sold.alpha = 0.9;
      sold.anchor.set(0.5, 0.5);
      sold.rotation = Math.PI / -8;
      slide.addChild(sold);
    }

    const price = new PIXI.Text(`$${parseFloat(slides.value[i].sale_price).toFixed(2)}`, {
      fontFamily: 'Lego',
      fontSize: Math.floor(padding * 3 * (Math.abs(props.aspectRatio - 0.8) + 0.8)),
      fill: 0xffff00,
      wordWrap: true,
      wordWrapWidth: size.x - padding * 2,
      align: aspectClass.value === 'portrait' ? 'left' : 'right',
      lineJoin: 'miter',
      miterLimit: 2,
      padding: padding,
      dropShadow: true,
      dropShadowColor: 'black',
      dropShadowBlur: Math.floor(padding / 3),
      stroke: 0x000000,
      strokeThickness: Math.floor(padding / 3),
    });

    if (aspectClass.value === 'portrait') {
      price.anchor.set(0,1)
      price.x = padding;
      price.y = size.y - (padding * 2);
    } else {
      price.anchor.set(1,1)
      price.x = size.x - padding;
      price.y = size.y - (padding / 3);
    }

    if (props.showPrice) slide.addChild(price);

    let alpha = [1,1,1]
    let x = [0,0,0]
    let y = [0,0,0]

    switch(props.transition) {
      case 'crossfade':
        alpha = [0,1,1]
        break;
      case 'drop':
        y = [-size.y,0,size.y]
        break;
      case 'slide':
        x = [size.x,0,0]
        break;
      case 'scroll':
        x = [size.x,0,-size.x]
        break;
      default:
        x[0] = (Math.floor(Math.random() * 2) ? 1 : -1) * size.x
        y[0] = (Math.floor(Math.random() * 2) ? 1 : -1) * size.y
        //alpha[0] = 0 //Math.floor(Math.random() * 2)
        // x[0] = (Math.floor(Math.random() * 3) - 1) * size.x
        // y[0] = (Math.floor(Math.random() * 3) - 1) * size.y
        //alpha[0] = x[0] || y[0] ? 1 : 0
        break;
    }

    slide.alpha = alpha[0];
    slide.x = x[0];
    slide.y = y[0];

    gsapParams.push([slide, {visible: true, ease: 'none'}, (holdD + fadeD) * (i - 1)])
    gsapParams.push([slide, {alpha: alpha[1], x:x[1], y:y[1], ease, duration: fadeD}, (holdD + fadeD) * i])
    gsapParams.push([slide, {visible: false, ease: 'none'}, (holdD + fadeD) * (i + 2)])
    if (x[2] !== x[1] || y[2] !== y[2]) {
      gsapParams.push([slide, {alpha: alpha[2], x:x[2], y:y[2], ease, duration: fadeD}, (holdD + fadeD) * (i + 1)])
    }

  }

  loading.value = false

  await new Promise(resolve => setTimeout(resolve, props.holdDuration + props.fadeDuration))
  gsap.ticker.fps(30);
  timeline = gsap.timeline()
  gsapParams.forEach(([slide, params, hold]) => timeline.to(slide, params, hold))

  if (!props.record) return
  // screen recorder
  stream = canvas.captureStream(30); // 30 fps
  // recorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
  recorder = new MediaRecorder(stream, { mimeType: 'video/mp4; codecs="avc1.42001e, mp4a.40.2"' });

  const recordedChunks = [];
  recorder.ondataavailable = (event) => {
    if (event.data.size > 0) recordedChunks.push(event.data);
  };

  recorder.onstop = async () => {
    clearTimeout(recorderTimer.value);
    recorderTimer.value = null;

    // const blob = new Blob(recordedChunks, { type: 'video/webm' });
    const blob = new Blob(recordedChunks, { type: 'video/mp4; codecs="avc1.42001e, mp4a.40.2"' });
    //await convertWebMtoMP4(blob);
    const url = URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    // downloadLink.download = `${new Date().toDateString()}-slideshow-recording.webm`;
    downloadLink.download = `${new Date().toDateString()}-slideshow-recording.mp4`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(url);
  };

  recorder.start()
  const vidLen = props.fadeDuration + (slides.value.length * (props.holdDuration + props.fadeDuration))
  recorderTimer.value = setTimeout(() => {recorder.stop()}, vidLen);
  console.log('stop recording in ' + vidLen +  'ms')
})

onBeforeUnmount(() => {
  if (recorder) {
    recorder.stop()
    recorder = null
    stream = null
  }

  if (timeline) {
    timeline.kill()
  }

  // Destroy PIXI app
  if (app) {
    app.destroy(true, { children: true, texture: true, baseTexture: true });
    app = null;
  }
})

</script>

<style lang="scss" scoped>
@import '@/assets/sass/_variables-esc.scss';

@function stroke($stroke, $color) {
  $shadow: ();
  $from: $stroke*-1;
  @for $i from $from through $stroke {
    @for $j from $from through $stroke {
      $shadow: append($shadow, $i*1px $j*1px 0 $color, comma);
    }
  }
  $shadow: append($shadow, 10px 10px 10px rgba(0,0,0,0.7), comma);
  @return $shadow;
}
.slideshow {
  width: clamp(400px, 100%, 89vh);
  margin: 0 auto;
  &.portrait { width: clamp(400px, 100%, 50vh) }
  &.landscape { width: clamp(400px, 150vh, 100%) }
  .slideshow-container {
    position: relative;
    width: 100%;
    overflow: hidden;
    border: 1px solid $gray-200;
    .slideshow-canvas {
      position: absolute;
      top:0; left:0;
      width: 100% !important;
      height: 100% !important;
    }
    &.square {
      padding-top: 100%;
    }
    &.portrait {
      padding-top: 177.78%
    }
    &.landscape {
      padding-top: 56.25%;
    }
  }
}
</style>
